/**
 * This component is rendered on the preview page when a funding option/transaction
 * triggers remittance transfer rule. This component displays a table of information
 * about how much the user is going to pay, how much the fees are, and how much
 * the recipient will receive.
 */
import PropTypes from 'prop-types'

import React from 'react'

import classNames from 'classnames'
import { StyleSheet, css, rtlConvertInline } from 'lib/paypalphrodite'
import enRTRStrings from '../../../../config/enRTRStrings'
import glamorous from 'glamorous'
import content from 'pp-react-l10n'
import { isDw2Repaint } from '../../experiments/dw2Repaint'
import { BodyText, CaptionText, v2grey600 } from '@paypalcorp/pp-react'
import ProductDetails from './product-details'
import { hasGuestFlowRevamp } from '../../experiments/guest'

let i18n = content('transfer/inc/rtr')

const styles = StyleSheet.create({
  txtAlignLeft: {
    textAlign: 'left',
  },
  txtAlignRight: {
    textAlign: 'right',
  },
  leftBlock: {
    width: '52%',
  },
  rightBlock: {
    width: '48%',
  },
  reviewInfo: {
    color: '#000000',
    paddingBottom: '20px',
  },
  rtrPrimaryText: {
    fontSize: '16px',
    color: '#2C2E2F',
    lineHeight: '24px',
  },
  rtrENSubText: {
    fontSize: '14px',
    color: '#687173',
    lineHeight: '20px',
    fontWeight: 'normal',
  },
  rtrPrimaryTextSmall: {
    fontSize: '0.9375rem',
    color: '#2C2E2F',
    lineHeight: '1.5rem',
  },
})

const ReviewInfoDiv = glamorous.div(
  {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems:
      isDw2Repaint() || hasGuestFlowRevamp() ? 'flex-start' : 'center',
    padding:
      isDw2Repaint() || hasGuestFlowRevamp() ? '0.5rem 0' : '0 16px 10px',
  },
  ({ showENSubTextRTR }) => {
    if (showENSubTextRTR) {
      return {
        alignItems: 'flex-start',
      }
    }
  }
)

class ReviewInfo extends React.Component {
  static propTypes = {
    isPurchase: PropTypes.bool.isRequired,
    recipient: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
    recipientCountry: PropTypes.string.isRequired,
    showDonateContent: PropTypes.bool,
    selectedInstallmentPlan: PropTypes.object,
    language: PropTypes.string,
    selectedFundingOption: PropTypes.shape({
      isRTR: PropTypes.bool,
      currency_exchange: PropTypes.object,
      displayAmounts: PropTypes.shape({
        paid: PropTypes.string, // the amount the sender is paying (including fees)
        received: PropTypes.string, // the amount the recipient will actually receive
        fee: PropTypes.string,
      }).isRequired,
    }).isRequired,
  }

  renderYouWillPay = () => {
    const {
      showDonateContent,
      selectedInstallmentPlan: { updatedTotal } = {},
      selectedFundingOption: { isRTR, displayAmounts: { paid, fee } = {} } = {},
      language = '',
    } = this.props

    let totalAmount = updatedTotal || paid
    const showENSubTextRTR =
      isRTR === true && language !== '' && language !== 'en'

    let youWillPayContentKey = 'rtr.youllPay'
    if (showDonateContent && !fee) {
      youWillPayContentKey = 'rtr.youllDonate'
    }

    return isDw2Repaint() || hasGuestFlowRevamp() ? (
      <>
        <ProductDetails
          showENSubTextRTR={showENSubTextRTR}
          stylesFromReviewPage={styles}
          /* Setting isDW2Repaint as true for ProductDetails even when going for Guest2.0 flow so that it can
          reuse the same code */
          isDw2Repaint={true}
          ReviewInfoDiv={ReviewInfoDiv}
        />
        <ReviewInfoDiv>
          <div>
            <BodyText strong>{i18n(youWillPayContentKey)}</BodyText>
            {showENSubTextRTR && (
              <CaptionText style={{ color: v2grey600 }}>
                {enRTRStrings[youWillPayContentKey]}
              </CaptionText>
            )}
          </div>
          <BodyText strong data-nemo="total-sender-pay">
            {totalAmount}
          </BodyText>
        </ReviewInfoDiv>
      </>
    ) : (
      <>
        <ProductDetails
          showENSubTextRTR={showENSubTextRTR}
          stylesFromReviewPage={styles}
        />
        <div
          className={`${css(
            rtlConvertInline(styles.txtAlignLeft)
          )} vx_text-5 txtBold`}
        >
          <div
            className={classNames(
              css(rtlConvertInline(styles.txtAlignLeft)),
              'col-xs-7',
              css(rtlConvertInline(styles.leftBlock))
            )}
          >
            {showENSubTextRTR ? (
              <>
                <div className={css(styles.rtrPrimaryText)}>
                  {i18n(youWillPayContentKey)}
                </div>
                <div className={css(styles.rtrENSubText)}>
                  {enRTRStrings[youWillPayContentKey]}
                </div>
              </>
            ) : (
              i18n(youWillPayContentKey)
            )}
          </div>
          <div
            className={classNames(
              css(rtlConvertInline(styles.txtAlignRight)),
              'col-xs-7',
              'totalAmount col-xs-5 txtAlignRight test_senderPay',
              css(rtlConvertInline(styles.rightBlock))
            )}
            data-nemo="total-sender-pay"
          >
            {totalAmount}
          </div>
        </div>
      </>
    )
  }

  renderRecipientWillGet = () => {
    const {
      recipient,
      displayName,
      selectedFundingOption: {
        isRTR,
        displayAmounts: { received: receivedAmount } = {},
      } = {},
      language = '',
    } = this.props
    const recipientGetsClasses = classNames(
      css(rtlConvertInline(styles.txtAlignLeft)),
      'col-xs-7 test_recipientEmail'
    )

    const showENSubTextRTR =
      isRTR === true && language !== '' && language !== 'en'
    const recipientDOM =
      isDw2Repaint() || hasGuestFlowRevamp() ? (
        displayName || recipient
      ) : (
        <span className={css(styles.rtrPrimaryTextSmall)}>
          {displayName || recipient}
        </span>
      )

    return isDw2Repaint() || hasGuestFlowRevamp() ? (
      <ReviewInfoDiv data-test="recipientGets">
        <div>
          <BodyText>
            {i18n('rtr.recipientGets', { recipient: recipientDOM })}
          </BodyText>
          {showENSubTextRTR && (
            <CaptionText style={{ color: v2grey600 }}>
              {`${recipientDOM} ${enRTRStrings['rtr.recipientGets']}`}
            </CaptionText>
          )}
        </div>
        <BodyText data-nemo="rtr-recipient-gets">{receivedAmount}</BodyText>
      </ReviewInfoDiv>
    ) : (
      <div className="recipientGets txtBold" data-test="recipientGets">
        <div className={recipientGetsClasses}>
          {showENSubTextRTR ? (
            <>
              <div className={css(styles.rtrPrimaryTextSmall)}>
                {i18n('rtr.recipientGets', {
                  recipient: recipientDOM,
                })}
              </div>
              <div className={css(styles.rtrENSubText)}>
                <>{recipientDOM}</>
                {` ${enRTRStrings['rtr.recipientGets']}`}
              </div>
            </>
          ) : (
            i18n('rtr.recipientGets', {
              recipient: recipientDOM,
            })
          )}
        </div>
        <div
          className={classNames(
            css(rtlConvertInline(styles.txtAlignRight)),
            'totalAmount col-xs-5 txtAlignRight'
          )}
          data-nemo="rtr-recipient-gets"
        >
          {receivedAmount}
        </div>
      </div>
    )
  }

  render() {
    const {
      isPurchase,
      selectedFundingOption: { isRTR, currency_exchange = {} } = {},
    } = this.props

    const fundingOptionsClasses = classNames(
      'selectedFundingOption-senderPay row senderPay',
      {
        isGoods: isPurchase,
      }
    )

    return (
      <div>
        <div
          className={fundingOptionsClasses}
          style={{
            ...((isDw2Repaint() || hasGuestFlowRevamp()) && {
              padding: '0.5rem',
              marginBottom: 0,
            }),
          }}
        >
          {this.renderYouWillPay()}

          {/* show when
           /* 1. is rtr, or
           /* 2. not goods & service AND cross currency
          */}
          {(isRTR ||
            (!isPurchase && Object.keys(currency_exchange).length > 0)) &&
            this.renderRecipientWillGet()}
          <div className="currencyConverter" />
        </div>
      </div>
    )
  }
}

export default ReviewInfo
